import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useState } from 'react'
import type {
  AssignmentResponse,
  PaginatedResponse,
  UserResponse,
  RoleResponse,
  ProductResponse,
  TenantResponse,
  BookmakerResponse,
  AuditLogResponse,
} from '@/api/types'

export function AssignmentDetailPage() {
  const { t } = useTranslation(['roles', 'common', 'audit'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const assignmentId = Number(id)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: assignmentData, isLoading } = useQuery({
    queryKey: ['assignment', assignmentId],
    queryFn: () => api.get<AssignmentResponse>(`/assignments/${assignmentId}`),
    enabled: !!assignmentId,
  })
  const assignment = assignmentData?.data

  // Lookup data
  const { data: usersData } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => api.get<PaginatedResponse<UserResponse>>('/users?offset=0&limit=500'),
  })
  const { data: rolesData } = useQuery({
    queryKey: ['roles-all'],
    queryFn: () => api.get<PaginatedResponse<RoleResponse>>('/roles?offset=0&limit=100'),
  })
  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>('/products?offset=0&limit=100'),
  })
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?offset=0&limit=100'),
  })
  const { data: bookmakersData } = useQuery({
    queryKey: ['bookmakers-all'],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>('/bookmakers?offset=0&limit=500'),
  })

  // Audit logs for this assignment
  const { data: auditData } = useQuery({
    queryKey: ['audit', 'assignment', assignmentId],
    queryFn: () => api.get<{ data: AuditLogResponse[]; total: number }>(`/audit?entity_type=assignment&entity_id=${assignmentId}&limit=50`),
    enabled: !!assignmentId,
  })
  const auditLogs = auditData?.data.data ?? []

  const userLookup = Object.fromEntries((usersData?.data.data ?? []).map((u) => [u.id, u]))
  const roleLookup = Object.fromEntries((rolesData?.data.data ?? []).map((r) => [r.id, r]))
  const productLookup = Object.fromEntries((productsData?.data.data ?? []).map((p) => [p.id, p]))
  const tenantLookup = Object.fromEntries((tenantsData?.data.data ?? []).map((t) => [t.id, t]))
  const siteLookup = Object.fromEntries((bookmakersData?.data.data ?? []).map((s) => [s.id, s]))

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/assignments/${assignmentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      navigate('/assignments')
    },
  })

  if (isLoading) return <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
  if (!assignment) return <p className="text-destructive">{t('assignments.notFound')}</p>

  const user = userLookup[assignment.user_id]
  const role = roleLookup[assignment.role_id]
  const product = role?.product_id ? productLookup[role.product_id] : null
  const tenant = tenantLookup[assignment.tenant_id]
  const bookmaker = assignment.bookmaker_id ? siteLookup[assignment.bookmaker_id] : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/assignments')}>
          <ArrowLeft className="h-4 w-4 mr-1" />{t('buttons.back', { ns: 'common' })}
        </Button>
        <h2 className="text-2xl font-semibold">{t('assignments.detail.title', { id: assignment.id })}</h2>
      </div>

      {/* Assignment Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('labels.details', { ns: 'common' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">{t('labels.user', { ns: 'common' })}</span>
              <p className="font-medium">{user?.username ?? user?.email ?? assignment.user_id}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('labels.role', { ns: 'common' })}</span>
              <p className="font-medium"><Badge>{role?.name ?? assignment.role_id}</Badge></p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('labels.product', { ns: 'common' })}</span>
              <p className="font-medium">{product?.name ?? (role?.product_id ? `#${role.product_id}` : '—')}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('labels.tenant', { ns: 'common' })}</span>
              <p className="font-medium">{tenant?.name ?? assignment.tenant_id}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('labels.bookmaker', { ns: 'common' })}</span>
              <p className="font-medium">{bookmaker?.name ?? (assignment.bookmaker_id ? assignment.bookmaker_id : t('labels.all', { ns: 'common' }))}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('labels.created', { ns: 'common' })}</span>
              <p className="font-medium">{new Date(assignment.created_at).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('labels.actions', { ns: 'common' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />{t('assignments.detail.removeAssignment')}
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('assignments.detail.removeAssignment')}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                {t('assignments.detail.removeConfirm', { role: role?.name ?? assignment.role_id, user: user?.username ?? user?.email ?? assignment.user_id })}
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                >
                  {deleteMutation.isPending ? t('assignments.detail.removing') : t('assignments.detail.confirmRemoval')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('title', { ns: 'audit' })}</CardTitle>
        </CardHeader>
        <CardContent>
          {auditLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('assignments.detail.noAuditLogs')}</p>
          ) : (
            <div className="border rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('fields.timestamp', { ns: 'audit' })}</th>
                    <th className="text-left p-3 font-medium">{t('fields.action', { ns: 'audit' })}</th>
                    <th className="text-left p-3 font-medium">{t('fields.actor', { ns: 'audit' })}</th>
                    <th className="text-left p-3 font-medium">{t('labels.details', { ns: 'common' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/audit/${log.id}`)}>
                      <td className="p-3 text-muted-foreground whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                      <td className="p-3"><Badge variant="secondary">{log.action}</Badge></td>
                      <td className="p-3">{log.actor_email ?? (log.actor_id ? `User #${log.actor_id}` : '—')}</td>
                      <td className="p-3 text-muted-foreground">{log.details ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
